interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  /** 可选每页条数；传入后显示切换控件 */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

export default function Pagination({
  page,
  pageSize,
  total,
  onChange,
  pageSizeOptions,
  onPageSizeChange,
  className = '',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const showPageSize = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 && !!onPageSizeChange;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => {
      if (totalPages <= 7) return true;
      if (p === 1 || p === totalPages) return true;
      if (Math.abs(p - safePage) <= 1) return true;
      return false;
    })
    .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
      if (i > 0) {
        const prev = arr[i - 1];
        if (p - prev > 1) acc.push('ellipsis');
      }
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className={`flex items-center justify-between gap-3 flex-wrap ${className}`}>
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          显示第 {from}-{to} 条，共 {total} 条
        </span>
        {showPageSize && (
          <label className="inline-flex items-center gap-1.5 text-xs text-foreground-500 whitespace-nowrap">
            每页
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              className="h-8 min-w-[4.5rem] px-2 rounded-md border border-background-200 bg-white text-sm text-foreground-700 outline-none hover:border-primary-300 focus:border-primary-400 transition-colors"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            条
          </label>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange(1)}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-400 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-skip-left-line text-sm" />
          </button>
          <button
            type="button"
            onClick={() => onChange(Math.max(1, safePage - 1))}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-left-s-line" />
          </button>

          {pages.map((item, i) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${i}`}
                className="w-8 h-8 flex items-center justify-center text-xs text-foreground-400"
              >
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onChange(item)}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  safePage === item
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {item}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => onChange(Math.min(totalPages, safePage + 1))}
            disabled={safePage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            disabled={safePage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-400 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-skip-right-line text-sm" />
          </button>
        </div>
      )}
    </div>
  );
}
